import Header from '../layouts/Header';
import React, { useState } from 'react';
import {
  Container, Button, Form, Table, Modal, Alert, Row, Col, Spinner, Card, Badge, Tabs, Tab
} from 'react-bootstrap';
import axiosInstance from '../services/axiosInterceptor';
import ThaiDateInput from '../components/ThaiDateInput'
import { toISODate } from '../utils/thaiDate'

const DormitoryTransfer = () => {
  const [activeTab, setActiveTab] = useState('search');
  const [studentSearch, setStudentSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentAllocation, setStudentAllocation] = useState(null);
  const [availableBuildings, setAvailableBuildings] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const [showMoveoutModal, setShowMoveoutModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  const [moveoutData, setMoveoutData] = useState({
    checkout_date: toISODate(new Date()),
    reason: ''
  });

  const [transferData, setTransferData] = useState({
    new_dorm_id: '',
    transfer_date: toISODate(new Date()),
    reason: ''
  });

  const handleSearchStudent = async (e) => {
    const key = e.target.value;
    setStudentSearch(key);

    if (key.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await axiosInstance.get(`/api/dormitory/students/search?searchKey=${key}`);
      if (response.data.success) {
        setSearchResults(response.data.data);
      }
    } catch (err) {
      console.error('Failed to search students:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectStudent = async (student) => {
    setSelectedStudent(student);
    setStudentAllocation(null);
    setAvailableBuildings([]);
    setError('');
    setSuccess('');
    setActiveTab('details');

    try {
      // Get student's current allocation
      const response = await axiosInstance.get(`/api/dormitory/students/${student.st_id}/allocation`);
      if (response.data.success) {
        setStudentAllocation(response.data.data);

        // Get available buildings for transfer (same gender village only)
        const vacancyResponse = await axiosInstance.get(
          `/api/dormitory/vacancies?gender=${student.st_gen}`
        );
        if (vacancyResponse.data.success) {
          setAvailableBuildings(vacancyResponse.data.data);
        }
      }
    } catch (err) {
      if (err.response?.status === 404) {
        // แท็บรายละเอียดขึ้นป้ายบอกอยู่แล้ว ไม่ต้องขึ้นแถบแดงซ้ำ
        setStudentAllocation(null);
      } else {
        setError(err.response?.data?.message || 'โหลดข้อมูลการเข้าพักของนักเรียนไม่สำเร็จ');
      }
    }

    setStudentSearch('');
    setSearchResults([]);
  };

  const handleMoveoutChange = (e) => {
    const { name, value } = e.target;
    setMoveoutData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTransferChange = (e) => {
    const { name, value } = e.target;
    setTransferData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMoveout = async () => {
    try {
      setError('');
      setSuccess('');
      setLoading(true);

      if (!studentAllocation?.alloc_id) {
        setError('ไม่พบข้อมูลการเข้าพักที่ใช้งานอยู่');
        return;
      }

      const response = await axiosInstance.post('/api/dormitory/checkout', {
        alloc_id: studentAllocation.alloc_id,
        checkout_date: moveoutData.checkout_date,
        reason: moveoutData.reason
      });

      if (response.data.success) {
        setSuccess(`ย้ายออก ${selectedStudent.st_name} จาก ${studentAllocation.dorm_name} เรียบร้อยแล้ว`);
        setShowMoveoutModal(false);
        setSelectedStudent(null);
        setStudentAllocation(null);
        setMoveoutData({
          checkout_date: toISODate(new Date()),
          reason: ''
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'ย้ายนักเรียนออกจากหอพักไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async () => {
    try {
      setError('');
      setSuccess('');
      setLoading(true);

      if (!studentAllocation?.alloc_id || !transferData.new_dorm_id) {
        setError('กรุณาเลือกอาคารปลายทาง');
        return;
      }

      const response = await axiosInstance.post('/api/dormitory/transfer', {
        alloc_id: studentAllocation.alloc_id,
        new_dorm_id: transferData.new_dorm_id,
        transfer_date: transferData.transfer_date,
        reason: transferData.reason
      });

      if (response.data.success) {
        const newBuilding = availableBuildings.find(b => b.dorm_id == transferData.new_dorm_id);
        setSuccess(`ย้าย ${selectedStudent.st_name} จาก ${studentAllocation.dorm_name} ไป ${newBuilding?.dorm_name} เรียบร้อยแล้ว`);
        setShowTransferModal(false);
        setSelectedStudent(null);
        setStudentAllocation(null);
        setTransferData({
          new_dorm_id: '',
          transfer_date: toISODate(new Date()),
          reason: ''
        });
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'ย้ายนักเรียนไปอาคารใหม่ไม่สำเร็จ';
      const code = err.response?.data?.code;

      if (code === 'NEW_DORM_FULL') {
        setError('อาคารปลายทางเต็มแล้ว กรุณาเลือกอาคารอื่น');
      } else if (code === 'GENDER_MISMATCH_NEW_DORM') {
        setError('เพศของนักเรียนไม่ตรงกับอาคารปลายทาง');
      } else {
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const genderBadge = (gender) => {
    if (gender === 'm') return <Badge bg="primary">👨 ชาย</Badge>;
    return <Badge bg="danger">👩 หญิง</Badge>;
  };

  return (
    <React.Fragment>
      <Header />
    <main className="main main-app p-3 p-lg-4">
      <Container fluid className="px-0">
        <h2 className="mb-4">จัดการย้ายหอและย้ายออก</h2>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

      <Tabs
        id="dorm-transfer-tabs"
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4"
      >
        {/* Tab 1: Search Student */}
        <Tab eventKey="search" title="🔍 ค้นหานักเรียน">
          <Card className="mt-4">
            <Card.Body>
              <h5 className="mb-3">ค้นหานักเรียน</h5>
              <Form.Group className="mb-4">
                <Form.Label>เลขประจำตัวหรือชื่อนักเรียน</Form.Label>
                <Form.Control
                  type="text"
                  value={studentSearch}
                  onChange={handleSearchStudent}
                  placeholder="กรอกเลขประจำตัวหรือชื่อนักเรียน..."
                />
                <Form.Text className="text-muted">พิมพ์อย่างน้อย 2 ตัวอักษรเพื่อค้นหา</Form.Text>
              </Form.Group>

              {searchLoading && (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                </div>
              )}

              {searchResults.length > 0 && (
                <Table responsive striped bordered hover>
                  <thead className="table-light">
                    <tr>
                      <th>เลขประจำตัว</th>
                      <th>ชื่อ</th>
                      <th>เพศ</th>
                      <th>สถานะ</th>
                      <th>เลือก</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map(student => (
                      <tr key={student.st_id}>
                        <td><strong>{student.st_idnumber}</strong></td>
                        <td>{student.st_name}</td>
                        <td>{genderBadge(student.st_gen)}</td>
                        <td>
                          {student.st_status === '1' ? (
                            <Badge bg="success">✓ ใช้งาน</Badge>
                          ) : (
                            <Badge bg="danger">✗ ไม่พร้อมใช้งาน</Badge>
                          )}
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleSelectStudent(student)}
                          >
                            เลือก
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              {studentSearch.length >= 2 && searchResults.length === 0 && !searchLoading && (
                <Alert variant="info">ไม่พบนักเรียนตามคำค้นหา</Alert>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* Tab 2: Student Details & Actions */}
        <Tab eventKey="details" title={`📋 รายละเอียดนักเรียน ${selectedStudent ? '✓' : ''}`}>
          <Card className="mt-4">
            <Card.Body>
              {!selectedStudent ? (
                <Alert variant="info">กรุณาค้นหาและเลือกนักเรียนก่อน</Alert>
              ) : (
                <>
                  <Row className="mb-4">
                    <Col md={6}>
                      <h5 className="mb-3">ข้อมูลนักเรียน</h5>
                      <p><strong>เลขประจำตัว:</strong> {selectedStudent.st_idnumber}</p>
                      <p><strong>ชื่อ:</strong> {selectedStudent.st_name}</p>
                      <p><strong>เพศ:</strong> {genderBadge(selectedStudent.st_gen)}</p>
                      <p>
                        <strong>สถานะ:</strong>{' '}
                        {selectedStudent.st_status === '1' ? (
                          <Badge bg="success">✓ ใช้งาน</Badge>
                        ) : (
                          <Badge bg="danger">✗ ไม่พร้อมใช้งาน</Badge>
                        )}
                      </p>
                    </Col>
                    <Col md={6}>
                      {studentAllocation ? (
                        <>
                          <h5 className="mb-3">ข้อมูลการเข้าพักปัจจุบัน</h5>
                          <p><strong>หมู่หอพัก:</strong> {studentAllocation.village_name}</p>
                          <p><strong>อาคาร:</strong> {studentAllocation.dorm_name}</p>
                          <p><strong>รหัสอาคาร:</strong> {studentAllocation.dorm_code}</p>
                          <p>
                            <strong>วันที่เข้าพัก:</strong>{' '}
                            {new Date(studentAllocation.checkin_date).toLocaleDateString()}
                          </p>
                        </>
                      ) : (
                        <Alert variant="warning">
                          นักเรียนคนนี้ยังไม่มีข้อมูลการเข้าพักที่ใช้งานอยู่
                        </Alert>
                      )}
                    </Col>
                  </Row>

                  {studentAllocation && (
                    <Row className="mt-4">
                      <Col md={6}>
                        <Button
                          variant="warning"
                          size="lg"
                          className="w-100"
                          onClick={() => setShowMoveoutModal(true)}
                        >
                          🚪 ย้ายออกจากหอพัก
                        </Button>
                        <small className="text-muted d-block text-center mt-2">
                          ยุติการพักหอของนักเรียนคนนี้
                        </small>
                      </Col>
                      <Col md={6}>
                        <Button
                          variant="info"
                          size="lg"
                          className="w-100"
                          onClick={() => setShowTransferModal(true)}
                          disabled={availableBuildings.length === 0}
                        >
                          🔄 ย้ายไปอาคารอื่น
                        </Button>
                        <small className="text-muted d-block text-center mt-2">
                          ย้ายนักเรียนไปยังอาคารหอพักอื่น
                        </small>
                      </Col>
                    </Row>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Modal: Move Out */}
      <Modal show={showMoveoutModal} onHide={() => setShowMoveoutModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>📤 ย้ายออกจากหอพัก</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {studentAllocation && (
            <>
              <Alert variant="warning">
                คุณกำลังจะย้าย <strong>{selectedStudent.st_name}</strong> ออกจาก{' '}
                <strong>{studentAllocation.dorm_name}</strong>
              </Alert>

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>วันที่ย้ายออก</Form.Label>
                  <ThaiDateInput
                    name="checkout_date"
                    value={moveoutData.checkout_date}
                    onChange={handleMoveoutChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>เหตุผลที่ย้ายออก</Form.Label>
                  <Form.Select
                    name="reason"
                    value={moveoutData.reason}
                    onChange={handleMoveoutChange}
                  >
                    <option value="">-- เลือกเหตุผล --</option>
                    <option value="graduation">สำเร็จการศึกษา</option>
                    <option value="resigned">ลาออก/พ้นสภาพ</option>
                    <option value="transfer">ย้ายไปสถานศึกษาอื่น</option>
                    <option value="personal">เหตุผลส่วนตัว</option>
                    <option value="discipline">เหตุผลด้านระเบียบวินัย</option>
                    <option value="other">อื่น ๆ</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>หมายเหตุเพิ่มเติม</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="note"
                    rows={3}
                    placeholder="รายละเอียดเพิ่มเติมเกี่ยวกับการย้ายออก..."
                  />
                </Form.Group>
              </Form>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMoveoutModal(false)}>
            ยกเลิก
          </Button>
          <Button
            variant="danger"
            onClick={handleMoveout}
            disabled={loading}
          >
            {loading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
            ยืนยันการย้ายออก
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal: Transfer */}
      <Modal show={showTransferModal} onHide={() => setShowTransferModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>🔄 ย้ายไปอาคารใหม่</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {studentAllocation && (
            <>
              <Alert variant="info">
                กำลังย้าย <strong>{selectedStudent.st_name}</strong> จาก{' '}
                <strong>{studentAllocation.dorm_name}</strong> ไปยังอาคารใหม่
              </Alert>

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label><strong>อาคารปลายทาง</strong></Form.Label>
                  {availableBuildings.length === 0 ? (
                    <Alert variant="warning" className="mb-0">
                      ไม่มีอาคารว่างสำหรับเพศของนักเรียนคนนี้
                    </Alert>
                  ) : (
                    <Form.Select
                      name="new_dorm_id"
                      value={transferData.new_dorm_id}
                      onChange={handleTransferChange}
                    >
                      <option value="">-- เลือกอาคาร --</option>
                      {availableBuildings.map(building => {
                        const vacant = building.vacant_count;
                        return (
                          <option key={building.dorm_id} value={building.dorm_id}>
                            {building.dorm_name} ({building.current_occupancy}/{building.capacity_total}, ว่าง {vacant} ที่)
                          </option>
                        );
                      })}
                    </Form.Select>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>วันที่ย้ายหอ</Form.Label>
                  <ThaiDateInput
                    name="transfer_date"
                    value={transferData.transfer_date}
                    onChange={handleTransferChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>เหตุผลในการย้ายหอ</Form.Label>
                  <Form.Select
                    name="reason"
                    value={transferData.reason}
                    onChange={handleTransferChange}
                  >
                    <option value="">-- เลือกเหตุผล --</option>
                    <option value="building_upgrade">สิ่งอำนวยความสะดวกดีกว่า</option>
                    <option value="closer_to_campus">ใกล้สถานศึกษามากกว่า</option>
                    <option value="friend_request">ความประสงค์ส่วนตัว/อยู่กับเพื่อน</option>
                    <option value="health_reasons">เหตุผลด้านสุขภาพ</option>
                    <option value="other">อื่น ๆ</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>หมายเหตุเพิ่มเติม</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="รายละเอียดเพิ่มเติมเกี่ยวกับการย้ายหอ..."
                  />
                </Form.Group>
              </Form>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTransferModal(false)}>
            ยกเลิก
          </Button>
          <Button
            variant="primary"
            onClick={handleTransfer}
            disabled={loading || !transferData.new_dorm_id}
          >
            {loading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
            ยืนยันการย้ายหอ
          </Button>
        </Modal.Footer>
      </Modal>
      </Container>
    </main>
  </React.Fragment>
  );
};

export default DormitoryTransfer;
